import React, { useState, useEffect, useContext } from 'react';
import { AppContext } from 'views/App/context';
import {
  OrganizationPerformanceReportingConfig,
  UpdateOrganizationPerformanceReportingConfigObject,
} from '../../_types/performanceReporting';
import { useMutation, useQueryClient } from 'react-query';
import View from './components/View';
import Edit from './components/Edit';
import usePerformanceReportingSolutionConfig from './_data_sources/usePerformanceReportingSolutionConfig';
import useOrganizationPerformanceReportingConfig from './_data_sources/useOrganizationPerformanceReportingConfig';
import { updateOrganizationPerformanceReportingConfig } from './_data_sources/manageOrganizationPerformanceReportingConfig';
import { useManageSolutionsContext } from '../../context';
import SolutionCard from '../../components/SolutionCard';
import errorHelper from 'helpers/errorHelper';
import { ServerErrorResponse } from 'data_sources/constants';

const PerformanceReporting: React.FC = () => {
  const { selectedSolution } = useManageSolutionsContext();
  const { currentOrganizationId } = useContext(AppContext);
  const queryClient = useQueryClient();
  const [isSaveLoading, setIsSaveLoading] = useState(false);

  // Use React Query hooks for data fetching
  const { data: solutionConfig, isLoading: isLoadingSolutionConfig } = usePerformanceReportingSolutionConfig();
  const { data: organizationConfig, isLoading: isLoadingOrgConfig } = useOrganizationPerformanceReportingConfig(
    currentOrganizationId || '',
    { enabled: !!currentOrganizationId }
  );

  const updatePerformanceReportingConfigMutation = useMutation(
    async (config: UpdateOrganizationPerformanceReportingConfigObject) => {
      if (!currentOrganizationId) throw new Error('Missing organization id');
      return updateOrganizationPerformanceReportingConfig(currentOrganizationId, config);
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['performanceReportingSolutionConfig']);
        if (currentOrganizationId)
          queryClient.invalidateQueries(['organizationPerformanceReportingConfig', currentOrganizationId]);
      },
      onError: (error: any) => {
        // Parse error and show notification
        try {
          const errorData = JSON.parse(error.message);
          errorHelper.enqueueErrors(errorData as ServerErrorResponse);
        } catch (e) {
          // Fallback if error is not JSON
          errorHelper.enqueueErrors({
            message: error.message || 'An unexpected error occurred.',
            errorDetails: null,
          } as ServerErrorResponse);
        }
      },
    }
  );

  const [localConfig, setLocalConfig] = useState<OrganizationPerformanceReportingConfig | null>(null);

  // Initialize local config when data loads
  useEffect(() => {
    if (organizationConfig) {
      setLocalConfig(organizationConfig);
    } else if (solutionConfig && currentOrganizationId) {
      // If no org config exists, create a default one with all modules disabled
      const defaultConfig: OrganizationPerformanceReportingConfig = {
        globalExpiresAt: null,
        modules: solutionConfig.modules.map((module) => ({
          id: module.id,
          name: module.name,
          status: 'DISABLED',
          expiresAt: null,
        })),
      };
      setLocalConfig(defaultConfig);
    }
  }, [organizationConfig, solutionConfig, currentOrganizationId]);

  const handleConfigChange = (updatedConfig: OrganizationPerformanceReportingConfig) => {
    setLocalConfig(updatedConfig);
  };

  // Handle save button click
  const handleSaveClick = async () => {
    if (!localConfig) return;
    setIsSaveLoading(true);

    try {
      const updatePayload: UpdateOrganizationPerformanceReportingConfigObject = {
        globalExpiresAt: localConfig.globalExpiresAt,
        modules: localConfig.modules,
      };
      await updatePerformanceReportingConfigMutation.mutateAsync(updatePayload);
    } finally {
      setIsSaveLoading(false);
    }
  };

  // Handle cancel button click - reset localConfig from cached query data and invalidate queries
  const handleCancelClick = () => {
    // Reset localConfig immediately from cached query data
    const cachedSolutionConfig = queryClient.getQueryData(['performanceReportingSolutionConfig']);
    const cachedOrgConfig = currentOrganizationId
      ? queryClient.getQueryData(['organizationPerformanceReportingConfig', currentOrganizationId])
      : null;

    if (cachedOrgConfig) {
      setLocalConfig(cachedOrgConfig as OrganizationPerformanceReportingConfig);
    } else if (cachedSolutionConfig && currentOrganizationId) {
      // If no org config exists, create a default one with all modules disabled
      const defaultConfig: OrganizationPerformanceReportingConfig = {
        globalExpiresAt: null,
        modules: (cachedSolutionConfig as any).modules.map((module: { id: string; name: string }) => ({
          id: module.id,
          name: module.name,
          status: 'DISABLED',
          expiresAt: null,
        })),
      };
      setLocalConfig(defaultConfig);
    }

    // Invalidate queries to ensure we have the latest data
    queryClient.invalidateQueries(['performanceReportingSolutionConfig']);
    if (currentOrganizationId) {
      queryClient.invalidateQueries(['organizationPerformanceReportingConfig', currentOrganizationId]);
    }
  };

  const isLoading = isLoadingSolutionConfig || isLoadingOrgConfig;

  if (!localConfig) {
    return (
      <SolutionCard
        solutionKey={selectedSolution!.key}
        title={selectedSolution!.name}
        view={<div>Loading...</div>}
        edit={<div>Loading...</div>}
        isLoading={true}
      />
    );
  }

  return (
    <SolutionCard
      solutionKey={selectedSolution!.key}
      title={selectedSolution!.name}
      view={<View config={localConfig} />}
      edit={
        <Edit
          config={localConfig}
          onChange={handleConfigChange}
        />
      }
      onSave={handleSaveClick}
      onCancel={handleCancelClick}
      isLoading={isLoading || isSaveLoading}
    />
  );
};

export default PerformanceReporting;
